import React, { useEffect, useState } from 'react'
import './AddPumpModal.scss'
import { ModalWrapper } from 'Components'
import { handleError } from 'Helpers'
import { ToastContainer } from 'react-toastify'
import fillingStationsService, {
  PumpNozzle,
  PumpProductType,
} from 'BoostXpressAdmin/Services/fillingStationsService'
import PumpNozzleSelector from './PumpNozzleSelector'

interface AddPumpModalProps {
  isOpen: boolean
  onClose: () => void
  onSuccess: () => void
  stationId: string | undefined
  productTypes: PumpProductType[]
}

export interface AddPumpForm {
  fuelFactPumpId: string
  pumpName: string
  productType: PumpProductType
  isActive: boolean
  nozzles: PumpNozzle[]
}

const INITIAL_FORM_VALUES: AddPumpForm = {
  fuelFactPumpId: '',
  pumpName: '',
  productType: '',
  isActive: true,
  nozzles: [],
}

const AddPumpModal: React.FC<AddPumpModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  stationId,
  productTypes,
}) => {
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [values, setValues] = useState<AddPumpForm>(() => ({
    ...INITIAL_FORM_VALUES,
    productType: productTypes[0] ?? '',
  }))

  useEffect(() => {
    if (!isOpen) return

    setValues((currentValues) =>
      currentValues.productType && productTypes.includes(currentValues.productType)
        ? currentValues
        : { ...currentValues, productType: productTypes[0] ?? '' },
    )
  }, [isOpen, productTypes])

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target

    setValues((currentValues) => ({
      ...currentValues,
      [name]:
        type === 'checkbox'
          ? (e.target as HTMLInputElement).checked
          : name === 'productType'
            ? (value as PumpProductType)
            : value,
    }))
  }

  const resetForm = () =>
    setValues({
      ...INITIAL_FORM_VALUES,
      productType: productTypes[0] ?? '',
    })

  const handleClose = () => {
    if (isSubmitting) return
    resetForm()
    onClose()
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (
      !stationId ||
      !values.fuelFactPumpId.trim() ||
      !values.pumpName.trim() ||
      !values.productType
    ) {
      handleError({ response: { data: { error: 'Please fill in all required fields' } } })
      return
    }

    if (values.nozzles.length === 0) {
      handleError({ response: { data: { error: 'Select at least one nozzle' } } })
      return
    }

    setIsSubmitting(true)

    try {
      await fillingStationsService.addPump(stationId, {
        fuelFactPumpId: values.fuelFactPumpId.trim(),
        pumpName: values.pumpName.trim(),
        productType: values.productType,
        isActive: values.isActive,
        nozzles: values.nozzles,
      })
      resetForm()
      onSuccess()
      onClose()
    } catch (error) {
      handleError(error)
    } finally {
      setIsSubmitting(false)
    }
  }

  if (!isOpen) return null

  return (
    <ModalWrapper
      showModal={isOpen}
      closeModal={handleClose}
      title='Add New Pump'
      hideHeader={false}
    >
      <div className='add-pump-modal'>
        <form onSubmit={handleSubmit} className='add-pump-modal__form'>
          <div className='add-pump-modal__field'>
            <label className='add-pump-modal__label'>Fuel Fact Pump ID *</label>
            <input
              type='text'
              name='fuelFactPumpId'
              value={values.fuelFactPumpId}
              onChange={handleChange}
              className='form-control'
              placeholder='Enter pump ID'
              required
            />
          </div>

          <div className='add-pump-modal__field'>
            <label className='add-pump-modal__label'>Pump Name *</label>
            <input
              type='text'
              name='pumpName'
              value={values.pumpName}
              onChange={handleChange}
              className='form-control'
              placeholder='Enter pump name'
              required
            />
          </div>

          <div className='add-pump-modal__field'>
            <label className='add-pump-modal__label'>Product Type *</label>
            <select
              name='productType'
              value={values.productType}
              onChange={handleChange}
              className='form-control'
              required
              disabled={productTypes.length === 0}
            >
              <option value='' disabled>
                Select product
              </option>
              {productTypes.map((productType) => (
                <option key={productType} value={productType}>
                  {productType}
                </option>
              ))}
            </select>
            {productTypes.length === 0 ? (
              <p className='add-pump-modal__hint'>
                Add products to this station before registering a pump.
              </p>
            ) : null}
          </div>

          <PumpNozzleSelector
            value={values.nozzles}
            onChange={(nozzles) => setValues((currentValues) => ({ ...currentValues, nozzles }))}
          />

          <div className='add-pump-modal__field'>
            <label className='add-pump-modal__status-option'>
              <input
                type='checkbox'
                name='isActive'
                checked={values.isActive}
                onChange={handleChange}
              />
              <span>
                <strong>Active pump</strong>
                <small>Include this pump in station operations and reports.</small>
              </span>
            </label>
          </div>

          <div className='add-pump-modal__actions'>
            <button
              type='button'
              className='add-pump-modal__cancel-button'
              onClick={handleClose}
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button
              type='submit'
              className='add-pump-modal__submit-button'
              disabled={isSubmitting || productTypes.length === 0}
            >
              {isSubmitting ? 'Adding...' : 'Add Pump'}
            </button>
          </div>
        </form>
      </div>
      <ToastContainer />
    </ModalWrapper>
  )
}

export default AddPumpModal
